import { NotificationType, GetNotificationsQuery } from 'graphql/generated/scheme'
import { EVENT_NOTIFICATION_TYPES, OFFER_NOTIFICATION_TYPES } from 'containers/common'

export type NotificationItem = NonNullable<
	NonNullable<GetNotificationsQuery['notification']['listNotifications2']>['nodes'][number]
>

// Helper function to get custom property value
// item and pItem are typed as any because notification payloads have no generated schema type yet
export const getCustomProperty = (item: any, key: string): string | undefined => {
	return item.content?.defaultPayload?.customProperties?.find((pItem: any) => pItem.key === key)?.value
}

/**
 * The event avatar of an event notification, or undefined when the event has none. An event without
 * an avatar (a series' hidden parent event) arrives as the bare media-server URL — not an empty
 * string — which is not an image and would render broken.
 */
export const getNotificationEventAvatar = (item: any): string | undefined => {
	const src = getCustomProperty(item, 'EventAvatar')?.trim()
	if (!src) return undefined
	const mediaBase = String(import.meta.env.VITE_MEDIA_URL ?? '').replace(/\/+$/, '')
	return src.replace(/\/+$/, '') === mediaBase ? undefined : src
}

// Navigation handler
// item is typed as any — notification payload shape is not yet codegen'd
// setTransferringPieInfo is typed as any — owned by StoreContext which is untyped
export const getNotificationNavigation = (item: any, setTransferringPieInfo: any): string => {
	const notificationType = item.content?.defaultPayload?.notificationType

	switch (notificationType) {
		case NotificationType.CreatePie:
			return '/legacy/pie-hub/overview'
		case NotificationType.ReceiveSlices:
		case NotificationType.BuySlices:
		case NotificationType.SendSlices:
			return '/legacy/slices'
		case NotificationType.SomeoneBuySlices:
			return '/legacy/pie-hub/sales'
		case NotificationType.ReceiveReward:
		case NotificationType.GenerateRewardWinnersReminder:
		case NotificationType.AwardedReward:
		case NotificationType.RewardLoser:
			return `/legacy/pies/${getCustomProperty(item, 'PieSlug')}/legacy/rewards/${getCustomProperty(
				item,
				'RewardSlug'
			)}`
		case NotificationType.ReceiveAchievement:
			return `/profile?achievement=${getCustomProperty(item, 'AchievementId')}`
		case NotificationType.UserInviteAccepted:
		case NotificationType.UserPassedWaitingList:
		case NotificationType.NewWisherAdded:
			return '/legacy/pie-hub'
		case NotificationType.UserInvitesIncreased:
			return '/profile/invites'
		case NotificationType.AwardPie:
			return '/legacy/pie-hub/details'
		case NotificationType.WishedPieActivated:
			return `/legacy/pies/${getCustomProperty(item, 'PieSlug')}`
		case NotificationType.PieOwnershipTransferRequest:
			setTransferringPieInfo(item.content.defaultPayload.customProperties)
			return '/legacy/pie-hub'
		case NotificationType.EventCoHostInvitation:
		case NotificationType.EventPromoteInvitation:
		case NotificationType.EventSponsorInvitation:
		case NotificationType.EventCoordinatorInvitation:
		case NotificationType.EventTalentInvitation:
		case NotificationType.EventInvitation:
		case NotificationType.EventLinkOffer:
		case NotificationType.EventSeriesRoleInvitation:
		case NotificationType.ClubOfferUserSelected:
			return `/accept-invite/${getCustomProperty(item, 'FormattedInviteId')}`
		case NotificationType.EventApproved:
		case NotificationType.EventDeclined:
		case NotificationType.EventStarted:
		case NotificationType.EventCheckinSuccess:
		case NotificationType.EventPublishedToFollowers:
			return `/events/${getCustomProperty(item, 'EventSlug')}`
		// Winner tap carries ?winner=1 so the event page force-opens the "You've been selected"
		// modal even if the once-per-session guard would otherwise suppress it.
		case NotificationType.ContestWinnerSelected:
			return `/events/${getCustomProperty(item, 'EventSlug')}?winner=1`
		// A ticket was sent straight to this user: the event page opens its "You've received tickets" popup
		// from ?receivedTicket=<ticket id>. EventSlug is the landing event.
		case NotificationType.EventTicketReceived: {
			const receivedTicketId = getCustomProperty(item, 'ReceivedTicketId')
			const eventUrl = `/events/${getCustomProperty(item, 'EventSlug')}`
			return receivedTicketId ? `${eventUrl}?receivedTicket=${encodeURIComponent(receivedTicketId)}` : eventUrl
		}
		case NotificationType.UserAcceptedEventRole:
		case NotificationType.UserDeclinedEventRole:
			return `/manage/rep-clubs/events/${getCustomProperty(item, 'EventSlug')}`
		case NotificationType.EventRoleUpdate: {
			const roleType = getCustomProperty(item, 'RoleType')?.toLowerCase()
			const attendeeRole = getCustomProperty(item, 'AttendeeRole')?.toLowerCase()
			const navigateUrlRoot =
				['cohost', 'promoter'].includes(roleType || '') || ['coordinator'].includes(attendeeRole || '')
					? '/manage/rep-clubs'
					: ''
			return `${navigateUrlRoot}/events/${getCustomProperty(item, 'EventSlug')}`
		}
		case NotificationType.EventPending:
			return `/manage/rep-clubs/events/${getCustomProperty(item, 'EventSlug')}/approval`
		case NotificationType.ClubUserEnteredOffer:
		case NotificationType.ClubUserClaimedOffer:
			return `/rep-clubs/${getCustomProperty(item, 'ClubSlug')}/offers/${getCustomProperty(item, 'OfferId')}`
		case NotificationType.ContactImportStarted:
		case NotificationType.ContactImportCompleted:
		case NotificationType.ContactImportFailed:
			return `/manage/rep-clubs/contacts?importJobId=${getCustomProperty(item, 'ImportJobId')}`
		case NotificationType.ContactDeleteStarted:
		case NotificationType.ContactDeleteCompleted:
		case NotificationType.ContactDeleteFailed:
			return `/manage/rep-clubs/contacts?deleteJobId=${getCustomProperty(item, 'ImportJobId')}`
		case NotificationType.ContactInviteStarted:
		case NotificationType.ContactInviteCompleted:
		case NotificationType.ContactInviteFailed:
			return `/manage/rep-clubs/events/${getCustomProperty(item, 'EventSlug')}/invite?inviteJobId=${getCustomProperty(item, 'ImportJobId')}`
		case NotificationType.ContactMessageStarted:
		case NotificationType.ContactMessageCompleted:
		case NotificationType.ContactMessageFailed:
			return `/manage/rep-clubs/contacts?messageJobId=${getCustomProperty(item, 'ImportJobId')}`
		case NotificationType.HostMessageReceived:
		case NotificationType.HostMessageReplyReceived:
		case NotificationType.EventGroupMessageReceived:
			return '/manage/rep-clubs/inbox'
		case NotificationType.UserInviteAction: {
			const inviteToken = getCustomProperty(item, 'InviteToken')
			const jobId = getCustomProperty(item, 'JobId')
			if (inviteToken) return `/accept-contact-share/${encodeURIComponent(inviteToken)}`
			if (jobId) return `/manage/rep-clubs/contacts?shareJobId=${encodeURIComponent(jobId)}`
			return '/profile/notifications'
		}
		default:
			return '/profile/notifications'
	}
}

// Notification type configuration types
export type NotificationConfig =
	| {
			avatarKey: string
			title?: string
			titleKey?: string
			verifiedKey?: never
			tierKey?: never
			imageSrc?: never
	  }
	| {
			avatarKey: string
			verifiedKey: string
			tierKey?: string
			titleKey: string
			title?: never
			imageSrc?: never
	  }
	| {
			imageSrc: string
			title: string
			titleKey?: never
			avatarKey?: never
			verifiedKey?: never
			tierKey?: never
	  }
	  | {
			imageSrc: string
			titleKey: string
			title?: never
			avatarKey?: never
			verifiedKey?: never
			tierKey?: never
	  }

// Notification type configurations
export const NOTIFICATION_CONFIG: Partial<Record<NotificationType, NotificationConfig>> = {
	[NotificationType.ReceiveAchievement]: {
		avatarKey: 'AchievementAvatar',
		title: 'Achievement',
	},
	[NotificationType.ReceiveReward]: {
		avatarKey: 'RewardAvatar',
		title: 'Reward',
	},
	[NotificationType.GenerateRewardWinnersReminder]: {
		avatarKey: 'RewardAvatar',
		title: 'Reward',
	},
	[NotificationType.AwardedReward]: {
		avatarKey: 'RewardAvatar',
		title: 'Reward',
	},
	[NotificationType.RewardLoser]: {
		avatarKey: 'RewardAvatar',
		title: 'Reward',
	},
	[NotificationType.CreatePie]: {
		avatarKey: 'PieAvatar',
		titleKey: 'PieName',
	},
	[NotificationType.NewWisherAdded]: {
		avatarKey: 'PieAvatar',
		titleKey: 'PieName',
	},
	[NotificationType.WishedPieActivated]: {
		avatarKey: 'PieAvatar',
		titleKey: 'PieName',
	},
	[NotificationType.BuySlices]: {
		avatarKey: 'PieAvatar',
		titleKey: 'PieName',
	},
	[NotificationType.PieOwnershipTransferRequest]: {
		avatarKey: 'PieAvatar',
		titleKey: 'PieName',
	},
	[NotificationType.PieOwnershipTransferAccepted]: {
		avatarKey: 'PieAvatar',
		titleKey: 'PieName',
	},
	[NotificationType.PieOwnershipTransferRejected]: {
		avatarKey: 'PieAvatar',
		titleKey: 'PieName',
	},
	[NotificationType.SomeoneBuySlices]: {
		avatarKey: 'BuyerAvatar',
		verifiedKey: 'BuyerIsVerified',
		tierKey: 'BuyerTier',
		titleKey: 'BuyerName',
	},
	[NotificationType.SendSlices]: {
		avatarKey: 'ReceiverAvatar',
		verifiedKey: 'ReceiverIsVerified',
		tierKey: 'ReceiverTier',
		titleKey: 'ReceiverName',
	},
	[NotificationType.AwardPie]: {
		avatarKey: 'ReceiverAvatar',
		verifiedKey: 'ReceiverIsVerified',
		tierKey: 'ReceiverTier',
		titleKey: 'ReceiverName',
	},
	[NotificationType.ReceiveSlices]: {
		avatarKey: 'SenderAvatar',
		verifiedKey: 'SenderIsVerified',
		tierKey: 'SenderTier',
		titleKey: 'SenderName',
	},
	[NotificationType.UserInviteAccepted]: {
		imageSrc: '/images/default/pie-hub.png',
		title: 'Pie Invite',
	},
	[NotificationType.UserPassedWaitingList]: {
		imageSrc: '/images/default/pie-hub.png',
		title: 'Pie Invite',
	},
	[NotificationType.UserInvitesIncreased]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Loyalink',
	},
	[NotificationType.ContactImportStarted]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.ContactImportCompleted]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.ContactImportFailed]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.ContactDeleteStarted]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.ContactDeleteCompleted]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.ContactDeleteFailed]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.ContactInviteStarted]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.ContactInviteCompleted]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.ContactInviteFailed]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.ContactMessageStarted]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.ContactMessageCompleted]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.ContactMessageFailed]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contacts',
	},
	[NotificationType.HostMessageReceived]: {
		avatarKey: 'SenderAvatar',
		titleKey: 'SenderName',
		title: 'Inbox',
	},
	[NotificationType.HostMessageReplyReceived]: {
		avatarKey: 'SenderAvatar',
		titleKey: 'SenderName',
		title: 'Inbox',
	},
	// No SenderAvatar in this payload (unlike the two above) — use the event's own avatar instead.
	[NotificationType.EventGroupMessageReceived]: {
		avatarKey: 'EventAvatar',
		titleKey: 'SenderName',
		title: 'Inbox',
	},
	[NotificationType.UserInviteAction]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Contact Share',
	},
	[NotificationType.CreateEventRequestResolved]: {
		imageSrc: '/images/logos/logo-badge.svg',
		title: 'Event Creation Request',
	},
	// Rendered as the series' image (or the series icon) by the notification avatar components.
	[NotificationType.EventSeriesRoleInvitation]: {
		avatarKey: 'SeriesAvatar',
		titleKey: 'SeriesName',
	},
}

// Get notification title
// item is typed as any — notification payload shape is not yet codegen'd
export const getNotificationTitle = (item: any): string | null => {
	const notificationType = item.content?.defaultPayload?.notificationType as NotificationType | undefined
	const config = notificationType ? NOTIFICATION_CONFIG[notificationType] : undefined

	if (!config) {
		if (notificationType && EVENT_NOTIFICATION_TYPES.includes(notificationType as NotificationType)) {
			return getCustomProperty(item, 'EventName') || 'Event'
		}
		if (notificationType && OFFER_NOTIFICATION_TYPES.includes(notificationType as NotificationType)) {
			return getCustomProperty(item, 'OfferTitle') || 'Offer'
		}
		return null
	}

	if ('title' in config && config.title) {
		return config.title
	}

	if ('titleKey' in config && config.titleKey) {
		return getCustomProperty(item, config.titleKey) || null
	}

	return null
}

// Check if notification has action buttons (Accept/Deny)
export const hasNotificationActions = (notificationType: NotificationType): boolean => {
	return [
		NotificationType.EventCoHostInvitation,
		NotificationType.EventPromoteInvitation,
		NotificationType.EventSponsorInvitation,
		NotificationType.EventLinkOffer,
		NotificationType.ClubOfferUserSelected,
	].includes(notificationType)
}
